import { useState, useEffect, useRef } from "react";
import { Citation } from "../models/audit/citation";
import { isTextInputTarget } from "../lib/domGuards";
import { type BoundingBoxState } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";

interface UseCitationNavigationProps {
  filteredCitations: Citation[];
  onCitationSelect: (citation: Citation) => void;
  /** Whether arrow-key navigation is live (default true). The attribute-test
   * fold-out turns this off because its panes own their own keyboard nav; the
   * support-cell flows leave it on. */
  keyboardEnabled?: boolean;
  /** Called with the citation an arrow-key press steps to. Not called for
   * auto-repeat presses from a held key, or for a press at either end of the
   * list that leaves the selection where it was. */
  onKeyboardStep?: (citation: Citation) => void;
}

export function useCitationNavigation({
  filteredCitations,
  onCitationSelect,
  keyboardEnabled = true,
  onKeyboardStep,
}: UseCitationNavigationProps) {
  // Ref'd so toggling nav doesn't tear down and re-add the listener.
  const keyboardEnabledRef = useRef(keyboardEnabled);
  keyboardEnabledRef.current = keyboardEnabled;
  const onKeyboardStepRef = useRef(onKeyboardStep);
  onKeyboardStepRef.current = onKeyboardStep;
  const [activeEvidenceIndex, setActiveEvidenceIndex] = useState<number>(0);
  const activeEvidenceIndexRef = useRef(activeEvidenceIndex);
  activeEvidenceIndexRef.current = activeEvidenceIndex;
  const [currentBoundingBox, setCurrentBoundingBox] = useState<
    BoundingBoxState | undefined
  >(undefined);
  const [currentSelectionValue, setCurrentSelectionValue] = useState<
    string | undefined
  >(undefined);
  const [focusedStepId, setFocusedStepId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (filteredCitations.length > 0 && activeEvidenceIndex >= 0) {
      const idx = Math.max(
        0,
        Math.min(activeEvidenceIndex, filteredCitations.length - 1),
      );
      onCitationSelect(filteredCitations[idx]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEvidenceIndex, filteredCitations.length]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!keyboardEnabledRef.current) return;
      if (isTextInputTarget(e.target)) return;

      // Step to the previous/next DISTINCT evidence item. A source/screenshot
      // pair is two citations that render as ONE row, so plain ±1 would "stick"
      // on the pair for an extra press; skip same-evidence neighbours (keyed by
      // encoded string). Returns the same index at the ends, so it also clamps.
      const step = (from: number, dir: number) => {
        const keyAt = (i: number) => {
          const c = filteredCitations[i];
          if (!c) return undefined;
          return c.encodedCitationString || c.id;
        };
        const cur = keyAt(from);
        for (
          let i = from + dir;
          i >= 0 && i < filteredCitations.length;
          i += dir
        ) {
          if (keyAt(i) !== cur) return i;
        }
        return from;
      };

      const stepAndReport = (dir: number) => {
        e.preventDefault();
        const current = activeEvidenceIndexRef.current;
        const next = step(current, dir);
        // Advance the ref now: a second press before the re-render must step
        // from here, as the functional update this replaced did.
        activeEvidenceIndexRef.current = next;
        setActiveEvidenceIndex(next);
        const target = filteredCitations[next];
        // A press at either end leaves the index where it was: not a step.
        if (target && !e.repeat && next !== current) {
          onKeyboardStepRef.current?.(target);
        }
      };

      if (e.key === "ArrowLeft") {
        stepAndReport(-1);
      } else if (e.key === "ArrowRight") {
        stepAndReport(1);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [filteredCitations]);

  return {
    activeEvidenceIndex,
    setActiveEvidenceIndex,
    currentBoundingBox,
    setCurrentBoundingBox,
    currentSelectionValue,
    setCurrentSelectionValue,
    focusedStepId,
    setFocusedStepId,
  };
}
